import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface SoftGateProps {
  /** Whether to apply the blur overlay. When false renders children normally. */
  locked: boolean
  /** Feature display name shown in the overlay. */
  featureName: string
  children: ReactNode
}

/**
 * SoftGate blurs and overlays the content with an upgrade CTA.
 * Use for features where a preview is shown but the full content is premium.
 */
export function SoftGate({ locked, featureName, children }: SoftGateProps) {
  if (!locked) return <>{children}</>

  return (
    <div className="relative">
      {/* Blurred content — still in DOM for screen readers to detect the region */}
      <div aria-hidden="true" className="select-none pointer-events-none blur-sm opacity-40">
        {children}
      </div>

      {/* Overlay */}
      <div
        role="region"
        aria-label={`${featureName} preview — upgrade to unlock`}
        className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(4,4,16,0.85) 40%, rgba(4,4,16,0.95) 100%)',
        }}
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-deep/50 border border-violet/25">
          <svg
            className="w-5 h-5 text-gold"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
        </div>

        <div className="max-w-xs">
          <p className="text-star-white font-semibold mb-1">Unlock {featureName}</p>
          <p className="text-star-soft text-sm">
            This section is a Premium feature. Start your free 7-day trial.
          </p>
        </div>

        <Link
          to="/pricing"
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white text-sm font-medium hover:shadow-glow-violet transition-all"
        >
          Start free trial
        </Link>
      </div>
    </div>
  )
}
